import { exists, UnreachableError } from 'base/helpers';
import {
  ComponentPayload,
  NestedNode,
  NestedTagNode,
} from 'base/webflow/schema';
import classNames from 'classnames';
import React, { useContext } from 'react';
import { BoundsMeasurer } from './bounds';
import rendererStyles from './component_renderer.css';
import { playInteractionsDecorator } from './interactions';
import webflowStyles from './webflow_base.css';

export type ComponentContext = Readonly<{
  /** id -> name **/
  classNames: ReadonlyMap<string, string>;
  ix2: ComponentPayload['ix2'];
  bounds: BoundsMeasurer;
  breakpoints: BreakpointContext;
}>;

export type BreakpointContext = Readonly<{
  current: string;
  smaller: readonly string[];
  larger: readonly string[];
}>;

export type AddEffect = (
  node: NestedTagNode,
  ctx: ComponentContext,
  ref: HTMLElement,
) => () => void;

export type WrapFunction = (
  children: React.ReactNode,
  node: NestedTagNode,
  ctx: ComponentContext,
  ref: React.RefObject<HTMLDivElement>,
  classList: classNames.ArgumentArray,
) => React.ReactNode;

export type ReplaceClassNames = (
  classNames: ReadonlyArray<classNames.Argument>,
) => ReadonlyArray<classNames.Argument>;

export type NodeDecorator = (
  node: NestedTagNode,
  ctx: ComponentContext,
) => Iterable<{
  className?: classNames.Argument;
  replaceClassNames?: ReplaceClassNames;
  style?: Pick<
    React.CSSProperties,
    'transform' | 'transition' | 'display' | 'WebkitTextFillColor'
  >;
  attrs?: React.AllHTMLAttributes<unknown>;
  wrapChildren?: (children: React.ReactNode) => React.ReactNode;
  replaceValue?: string;
  addEffect?: AddEffect;
  wrap?: WrapFunction;
}>;

export function composeDecorators(
  ...fns: (NodeDecorator | undefined | null)[]
): NodeDecorator {
  const filtered = fns.filter(exists);
  return function* (...args) {
    for (const fn of filtered) {
      for (const decoration of fn(...args)) {
        yield decoration;
      }
    }
  };
}

export function componentHasMenus(component: ComponentPayload): boolean {
  return component.nodes.some(
    (n) => !n.text && (n.type === 'DropdownList' || n.type === 'NavbarWrapper'),
  );
}

export const makeDropdownDecorator = (opts: {
  readonly openMenus: boolean;
}): NodeDecorator => {
  return function* (node, _ctx) {
    if (
      (node.type === 'DropdownToggle' || node.type === 'DropdownList') &&
      opts.openMenus
    ) {
      yield { className: webflowStyles['w--open'] };
    }
  };
};

export const makeNavbarDecorator = (opts: {
  readonly openMenus: boolean;
}): NodeDecorator => {
  return function* (node, ctx) {
    if (node.data.navbar?.type === 'wrapper') {
      checkNodeTypes(node, 'NavbarWrapper');
      const { collapse: collapseBreakpoint } = node.data.navbar;
      const isCollapsed =
        [ctx.breakpoints.current, ...ctx.breakpoints.larger].includes(
          collapseBreakpoint,
        ) || collapseBreakpoint === 'all';
      if (isCollapsed) {
        yield { className: webflowStyles.navbarWrapperCollapsed };
      }
      if (isCollapsed && opts.openMenus) {
        yield { className: webflowStyles.openMenus };
      }
    }
  };
};

export const makeFormVisibilityDecorator = (
  opts: {
    readonly showFormFields: boolean;
    readonly showFormSuccess: boolean;
    readonly showFormError: boolean;
  } = {
    showFormFields: true,
    showFormSuccess: false,
    showFormError: false,
  },
): NodeDecorator =>
  function* (node, _ctx) {
    if (node.type === 'FormForm' && !opts.showFormFields) {
      yield { style: { display: 'none' } };
    }
    if (node.type === 'FormSuccessMessage' && !opts.showFormSuccess) {
      yield { style: { display: 'none' } };
    }
    if (node.type === 'FormErrorMessage' && !opts.showFormError) {
      yield { style: { display: 'none' } };
    }
  };

const SliderContext = React.createContext<{
  navInvert: boolean;
  navSpacing: number;
  slideCount: number;
} | null>(null);

export const makeSliderDecorator = (opts: {
  readonly sliderIndex: number;
}): NodeDecorator =>
  function* (node) {
    const { slider } = node.data;
    if (!slider) return;

    if (slider.type === 'wrapper') {
      checkNodeTypes(node, 'SliderWrapper');
      const slideCount =
        node.children
          .find((n) => !n.text && n.data.slider?.type === 'slides')
          ?.children.filter((c) => !c.text && c.data.slider?.type === 'slide')
          ?.length || 0;

      yield {
        wrapChildren: (children) => (
          <SliderContext.Provider
            value={{
              navInvert: slider.navInvert,
              navSpacing: slider.navSpacing,
              slideCount,
            }}
          >
            {children}
          </SliderContext.Provider>
        ),
      };
    } else if (slider.type === 'slide' || slider.type === 'Slider') {
      checkNodeTypes(node, 'SliderSlide');
      yield {
        style: {
          transform: `translateX(${-(opts.sliderIndex || 0) * 100}%)`,
          transition: 'transform 500ms ease',
        },
      };
    } else if (slider.type === 'arrow') {
      checkNodeTypes(node, 'SliderArrow');
      yield { className: webflowStyles[`w-slider-arrow-${slider.dir}`] };
    } else if (slider.type === 'nav') {
      checkNodeTypes(node, 'SliderNav');
      try {
        const context = useContext(SliderContext);
        if (!context) throw new Error('no slider context');
        yield {
          className: [
            webflowStyles['w-slider-nav'],
            // NOTE: update this to support other nav styles if needed
            webflowStyles['w-round'],
            context.navInvert && webflowStyles['w-slider-nav-invert'],
          ],
        };

        yield {
          wrapChildren: () => (
            <SliderDots
              slideCount={context.slideCount}
              activeIndex={opts.sliderIndex}
              navSpacing={context.navSpacing}
            />
          ),
        };
      } catch (e) {
        console.warn('[component_renderer] slider nav construction error', e);
      }
    } else if (slider.type === 'slides') {
    } else {
      throw new UnreachableError(slider);
    }
  };

const tabNamesCache = new WeakMap<NestedTagNode, readonly string[]>();
function getTabNames(wrapper: NestedTagNode): readonly string[] {
  if (tabNamesCache.has(wrapper)) return tabNamesCache.get(wrapper)!;
  const names: string[] = [];
  function walk(node: NestedTagNode) {
    if (node.data.tabs?.type === 'link') {
      names.push(node.data.attr?.['data-w-tab'] || '');
      return;
    }
    for (const child of node.children) {
      if (!child.text) walk(child);
    }
  }
  walk(wrapper);
  tabNamesCache.set(wrapper, names);
  return names;
}

const TabContext = React.createContext<{
  tabNames: readonly string[];
} | null>(null);

export const makeTabsDecorator = (opts: {
  readonly tabIndex: number;
}): NodeDecorator => {
  function getTabChildState(node: NestedTagNode): {
    isCurrent: boolean;
  } {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const context = useContext(TabContext);
    if (!context) {
      console.warn(`no tabs wrapper found for ${node.type}`, node, parent);
      return { isCurrent: false };
    }
    const myName = node.data.attr?.['data-w-tab'] || '';
    const tabIndex = context.tabNames.indexOf(myName);
    return { isCurrent: tabIndex === opts.tabIndex };
  }

  return function* (node) {
    const { tabs } = node.data;
    if (!tabs) return;

    if (tabs.type === 'wrapper') {
      checkNodeTypes(node, 'TabsWrapper');
      yield {
        className: webflowStyles['w-tabs'],
        wrapChildren: (children) => (
          <TabContext.Provider
            value={{
              tabNames: getTabNames(node),
            }}
          >
            {children}
          </TabContext.Provider>
        ),
      };
    } else if (tabs.type === 'link') {
      checkNodeTypes(node, 'TabsLink');
      const state = getTabChildState(node);
      yield {
        addEffect: preventClickEffect,
        className: [
          webflowStyles['w-tab-link'],
          state.isCurrent && webflowStyles['w--current'],
        ],
      };
    } else if (tabs.type === 'pane') {
      checkNodeTypes(node, 'TabsPane');
      const state = getTabChildState(node);
      yield {
        className: [
          webflowStyles['w-tab-pane'],
          state.isCurrent && webflowStyles['w--tab-active'],
        ],
      };
    } else if (tabs.type === 'menu') {
      checkNodeTypes(node, 'TabsMenu');
      yield { className: webflowStyles['w-tab-menu'] };
    } else if (tabs.type === 'content') {
      checkNodeTypes(node, 'TabsContent');
      yield { className: webflowStyles['w-tab-content'] };
    } else {
      console.warn(`Unknown tabs node data: ${JSON.stringify(tabs)}`);
    }
  };
};

export function countComponentTabs(component: ComponentPayload) {
  let count = 0;
  for (const node of component.nodes) {
    if (!node.text && node.data.tabs?.type === 'link') count++;
  }
  return count;
}

function SliderDots({
  slideCount,
  activeIndex,
  navSpacing,
}: {
  slideCount: number;
  activeIndex: number;
  navSpacing: number;
}) {
  return (
    <>
      {Array.from({ length: slideCount }).map((_, i) => (
        <div
          key={i}
          className={classNames(
            webflowStyles['w-slider-dot'],
            i === activeIndex && webflowStyles['w-active'],
          )}
          style={{
            marginLeft: navSpacing,
            marginRight: navSpacing,
          }}
        />
      ))}
    </>
  );
}

function checkNodeTypes(node: NestedNode, type: string) {
  if (!node.text && node.type === type) return;
  console.warn(
    `[component_renderer] Expected node type ${type}, got ${
      node.text ? '<text>' : node.type
    }`,
    node,
  );
}

/**
 * Implements basic features that do not require further configuration.
 **/
export const baseDecorator: NodeDecorator = function* (node, ctx) {
  const { grid, button, block } = node.data;
  yield {
    className: [
      block === 'inline' && webflowStyles['w-inline-block'],
      button && webflowStyles['w-button'],
      node.type && typeExtraClasses.get(node.type),
      getGridClass(grid),
    ],
  };
  yield* playInteractionsDecorator(node, ctx);

  /* Stop links from being interactive */
  if (node.tag === 'a' || node.tag === 'button') {
    yield {
      addEffect: preventClickEffect,
      className: rendererStyles.resetCursor,
      attrs: { tabIndex: -1 },
    };
  }
  /* Stop form elements from being interactive */
  if (
    node.tag === 'input' ||
    node.tag === 'textarea' ||
    node.tag === 'select'
  ) {
    yield {
      className: rendererStyles.disablePointerEvents,
      attrs: { tabIndex: -1 },
    };
  }

  if (node.type === 'FormSelect') {
    const options =
      (node.data.form?.type === 'select' && node.data.form.opts) || [];
    yield {
      wrapChildren: (children) => (
        <>
          {children}
          {options.map((option, i) => (
            <option key={i} value={option.v}>
              {option.t}
            </option>
          ))}
        </>
      ),
    };
  }
};

const preventClickEffect: AddEffect = (_node, _ctx, el) => {
  // Do not allow links to be clicked
  el.addEventListener('click', preventDefault);
  return () => el.removeEventListener('click', preventDefault);
};
function preventDefault(e: Event) {
  e.preventDefault();
}

function getGridClass(grid: NestedTagNode['data']['grid']): string | undefined {
  if (!grid) return;
  if (typeof grid !== 'string' || !gridClasses.has(grid)) {
    console.warn('[component_renderer] unknown grid type', grid);
    return;
  }
  return gridClasses.get(grid);
}

const gridClasses = new Map<string, string>([
  ['two-by-two', rendererStyles.twoByTwoGrid],
]);
const typeExtraClasses = new Map<string, string>([
  ['DropdownWrapper', webflowStyles['w-dropdown']],
  ['DropdownToggle', webflowStyles['w-dropdown-toggle']],
  ['DropdownList', webflowStyles['w-dropdown-list']],
  ['DropdownLink', webflowStyles['w-dropdown-link']],
  ['SliderWrapper', webflowStyles['w-slider']],
  ['SliderMask', webflowStyles['w-slider-mask']],
  ['SliderSlide', webflowStyles['w-slide']],
  ['FormTextInput', webflowStyles['w-input']],
  ['FormTextarea', webflowStyles['w-input']],
  ['FormSelect', webflowStyles['w-select']],
  ['FormButton', webflowStyles['w-button']],
  ['NavbarWrapper', webflowStyles['w-nav']],
  ['NavbarBrand', webflowStyles['w-nav-brand']],
  ['NavbarMenu', webflowStyles['w-nav-menu']],
  ['NavbarButton', webflowStyles['w-nav-button']],
  ['NavbarLink', webflowStyles['w-nav-link']],
  ['MapWidget', rendererStyles.mapWidget],
]);
